import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconPlus, IconX } from '@tabler/icons-react';
import type { ProductsListItem } from '@techgitter/contracts';
import { fetchInventory, fetchLicenseProducts, setLicenseKeyFlag } from '../../services/inventoryApi';
import { Chip } from '../chip';
import { CARD_SX } from '../../styles/designSystem';

/**
 * Manage which commercial products are resale-license SKUs (cr001_is_license_key).
 * Mirrors the is_bundle toggle on the Bundles admin page: search any product,
 * flag it, and un-flag listed ones. The flag drives the receive "paste keys"
 * field and the ticket "Provision license" picker.
 */
export const LicenseProductsCard: React.FC = () => {
  const [flagged, setFlagged] = useState<ProductsListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null,
  );

  // Product picker (search all products, not just flagged ones).
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ProductsListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<ProductsListItem | null>(null);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const items = await fetchLicenseProducts(signal);
      setFlagged(items);
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      setSnackbar({
        message: e instanceof Error ? e.message : 'Failed to load license products',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  // Debounced server search.
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setOptions([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      fetchInventory({ page: 1, pageSize: 50, search: q, sort: 'displayName', order: 'asc', signal: controller.signal })
        .then((res) => setOptions(res.items.filter((p) => !p.disabled)))
        .catch((e) => {
          if (!(e instanceof Error && e.name === 'AbortError')) setOptions([]);
        })
        .finally(() => setSearching(false));
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [input]);

  const flaggedIds = useMemo(() => new Set(flagged.map((p) => p.id)), [flagged]);
  const pickable = useMemo(() => options.filter((o) => !flaggedIds.has(o.id)), [options, flaggedIds]);

  const handleAdd = async () => {
    if (!selected) return;
    setBusyId(selected.id);
    try {
      await setLicenseKeyFlag(selected.id, true);
      setSnackbar({ message: `${selected.displayName || selected.sku} marked as a license product`, severity: 'success' });
      setSelected(null);
      setInput('');
      await refresh();
    } catch (e) {
      setSnackbar({ message: e instanceof Error ? e.message : 'Failed to flag product', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (product: ProductsListItem) => {
    setBusyId(product.id);
    try {
      await setLicenseKeyFlag(product.id, false);
      setSnackbar({ message: `${product.displayName || product.sku} unmarked`, severity: 'success' });
      await refresh();
    } catch (e) {
      setSnackbar({ message: e instanceof Error ? e.message : 'Failed to unflag product', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={[CARD_SX, { p: 2 }]}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#646464', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
        License products
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Products flagged here are sold as redeemable keys held in stock — the receipt dialog prompts
        for keys and the ticket can provision them.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Autocomplete
          sx={{ minWidth: 360 }}
          size="small"
          options={pickable}
          loading={searching}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          onInputChange={(_, v) => setInput(v)}
          filterOptions={(x) => x}
          getOptionLabel={(o) => o.displayName || o.sku || ''}
          getOptionKey={(o) => o.id}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText={input.trim().length < 2 ? 'Type to search products…' : 'No matching products'}
          renderInput={(params) => <TextField {...params} label="Find a product to flag" />}
        />
        <Button
          variant="contained"
          startIcon={<IconPlus size={16} stroke={1.5} />}
          disabled={!selected || busyId !== null}
          onClick={handleAdd}
        >
          Mark as license product
        </Button>
      </Stack>

      {loading ? (
        <CircularProgress size={20} />
      ) : flagged.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No products flagged yet.
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {flagged.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: '0.5px solid #e2eaf3',
                borderRadius: '6px',
                pl: 1,
                pr: 0.25,
                py: 0.25,
              }}
            >
              <Chip tone="info">{p.displayName || p.sku}</Chip>
              <Tooltip title="Unmark (no longer a license product)">
                <span>
                  <IconButton size="small" disabled={busyId === p.id} onClick={() => handleRemove(p)}>
                    <IconX size={14} stroke={1.5} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
